import { AlertTriangle, Check, CircleHelp, Download, FileUp, GitFork, Loader2, Redo2, RefreshCw, Route, Search, Settings, Share2, Undo2, UsersRound, List, ChevronDown, ChartColumn, CalendarClock, Images, BookOpen, ListChecks, Users, Compass, LibraryBig, Map as MapIcon, type LucideIcon } from 'lucide-react';
import { Fragment, lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { applyAppearance, useSystemDark } from './appearance';
import { AddRelativeDialog } from './components/AddRelativeDialog';
import { BulkEditDialog } from './components/BulkEditDialog';
import { CitationEditor } from './components/CitationEditor';
import { ContextMenuHost, handleContextMenu } from './components/ContextMenu';
import { Modal } from './components/common';
import { CommandPalette } from './components/CommandPalette';
import { ConfirmDialog, HelpDialog, MergeDialog, SettingsDialog, TreesDialog } from './components/Dialogs';
import { ExportDialog, ImportDialog, parseDrop } from './components/ImportExport';
import { PersonEditor } from './components/PersonEditor';
import { PersonPanel } from './components/PersonPanel';
import { RelationshipEditor } from './components/RelationshipEditor';
import { startSync, SyncDialog } from './components/SyncDialog';
import { ViewBoundary } from './components/ViewBoundary';
import { Welcome } from './components/Welcome';
import { flagSlot } from './themes';
import { applyHash, dismissToast, flushSave, getState, hasPanel, init, keepUnsaved, openDialog, openSection, redo, sectionOf, SECTIONS, selectPerson, setView, toast, undo, useApp, type Dialog, type Section, type View } from './store';
import { CountdownsView } from './views/CountdownsView';
import { GroupDetails, GroupEditor, GroupsView } from './views/GroupsView';
import { MediaDetails, MediaView } from './views/MediaView';
import { NetworkView } from './views/NetworkView';
import { PeopleView } from './views/PeopleView';
import { RelateView } from './views/RelateView';
import { ReportsView } from './views/ReportsView';
import { SourceEditor, SourcesView } from './views/SourcesView';
import { TodoEditor, TodosView } from './views/TodosView';
import { TreeView } from './views/TreeView';

// The map (Leaflet) loads when it's first opened.
const MapView = lazy(() => import('./views/MapView'));

/** `long` is the full name where there's room (phone tabs, tooltips); `sub` is a second line under the label in the sidebar. */
const VIEW_INFO: Record<View, { label: string; long?: string; sub?: string; Icon: LucideIcon }> = {
  tree: { label: 'Tree', Icon: GitFork },
  network: { label: 'Network', Icon: Share2 },
  people: { label: 'People', Icon: List },
  groups: { label: 'Groups', Icon: UsersRound },
  relate: { label: 'Relate', Icon: Route },
  reports: { label: 'Reports', Icon: ChartColumn },
  countdowns: { label: 'Countdowns', Icon: CalendarClock },
  map: { label: 'Map', Icon: MapIcon },
  media: { label: 'Media', Icon: Images },
  sources: { label: 'Sources', Icon: BookOpen },
  todos: { label: 'To-dos', long: 'To-dos & suggestions', sub: '& suggestions', Icon: ListChecks },
};

const SECTION_ICONS: Record<Section, LucideIcon> = { tree: GitFork, people: Users, explore: Compass, research: LibraryBig };

/** Every view in rail order (grouped by section). Number keys 1–9 then 0 open the first ten. */
const ALL_VIEWS: View[] = SECTIONS.flatMap((s) => s.views);
const viewKey = (v: View) => (ALL_VIEWS.indexOf(v) < 10 ? String((ALL_VIEWS.indexOf(v) + 1) % 10) : '');

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function App() {
  const ready = useApp((s) => s.ready);
  const tree = useApp((s) => s.tree);
  const theme = useApp((s) => s.settings.theme);
  const themeId = useApp((s) => s.settings.themeId);
  const systemDark = useSystemDark();
  const dialogs = useApp((s) => s.dialogs);
  const toasts = useApp((s) => s.toasts);

  useEffect(() => {
    init();
    const onPop = () => applyHash();
    // Save right away when the page goes to the background: phones often close background tabs
    // without ever sending pagehide. A closing page can't finish that save, so keepUnsaved() also
    // writes the changes where the next start finds them.
    const onHide = () => {
      keepUnsaved();
      void flushSave();
    };
    const onVisibility = () => document.visibilityState === 'hidden' && onHide();
    window.addEventListener('popstate', onPop);
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('popstate', onPop);
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  // Before paint, so a saved theme never flashes the default colors.
  useLayoutEffect(() => applyAppearance({ theme, themeId }, systemDark), [theme, themeId, systemDark]);

  useEffect(() => {
    if (tree) document.title = `${tree.name} · Relationships`;
    else document.title = 'Relationships: family trees for every kind of family';
  }, [tree]);

  // Global keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey;
      const s = getState();
      const target = e.target as HTMLElement;
      const typing = !!target.closest?.('input, textarea, select, [contenteditable]');
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (s.tree && !s.dialogs.some((d) => d.type === 'palette')) openDialog({ type: 'palette' });
        return;
      }
      if (typing || s.dialogs.length) return;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || !s.tree) return;
      if (e.key === '/') {
        e.preventDefault();
        setView('people');
        setTimeout(() => document.querySelector<HTMLInputElement>('[data-global-search]')?.focus(), 30);
      } else if (e.key === '?') openDialog({ type: 'help' });
      else if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        openDialog({ type: 'new-person' });
      } else if ((e.key === 'e' || e.key === 'E') && s.selectedId) {
        e.preventDefault();
        openDialog({ type: 'edit-person', personId: s.selectedId });
      } else if (e.key === 'Escape' && s.selectedId) selectPerson(null);
      else if (/^[0-9]$/.test(e.key)) {
        const v = ALL_VIEWS.find((x) => viewKey(x) === e.key);
        if (v) setView(v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Drop a file anywhere to import it.
  useEffect(() => {
    const over = (e: DragEvent) => {
      if (e.dataTransfer?.types.includes('Files')) e.preventDefault();
    };
    const drop = async (e: DragEvent) => {
      if (!e.dataTransfer?.files.length) return;
      // Always cancel the drop, or the browser navigates away to show the file.
      e.preventDefault();
      if (getState().dialogs.some((d) => d.type === 'import')) return;
      const pending = parseDrop(e.dataTransfer);
      if (!pending) return;
      try {
        const parsed = await pending;
        openDialog({ type: 'import', initial: parsed, mode: 'merge' });
      } catch (err) {
        toast((err as Error).message, 'error');
      }
    };
    window.addEventListener('dragover', over);
    window.addEventListener('drop', drop);
    window.addEventListener('contextmenu', handleContextMenu);
    return () => {
      window.removeEventListener('dragover', over);
      window.removeEventListener('drop', drop);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);

  if (!ready)
    return (
      <div className="empty" style={{ height: '100%' }}>
        <Loader2 className="spin" /> Loading…
      </div>
    );

  return (
    <>
      {tree ? <Shell /> : <Welcome />}
      {dialogs.map((d, i) => (
        <DialogHost key={i} d={d} />
      ))}
      <ContextMenuHost />
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            <span>{t.text}</span>
            {t.action && (
              <button
                onClick={() => {
                  t.action!.run();
                  dismissToast(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            <button onClick={() => dismissToast(t.id)} aria-label="Dismiss" style={{ textDecoration: 'none' }}>
              ×
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

function DialogHost({ d }: { d: Dialog }) {
  switch (d.type) {
    case 'edit-person': return <PersonEditor personId={d.personId} field={d.field} />;
    case 'person-details':
      return (
        <Modal className="details-modal" ariaLabel="Person details">
          <PersonPanel key={d.personId} personId={d.personId} inDialog />
        </Modal>
      );
    case 'add-relative': return <AddRelativeDialog personId={d.personId} kind={d.kind} existing={d.existing} />;
    case 'edit-relationship': return <RelationshipEditor relId={d.relId} />;
    case 'new-person': return <PersonEditor />;
    case 'import': return <ImportDialog initial={d.initial} mode={d.mode} />;
    case 'export': return <ExportDialog picture={d.picture} />;
    case 'trees': return <TreesDialog />;
    case 'settings': return <SettingsDialog />;
    case 'sync': return <SyncDialog />;
    case 'help': return <HelpDialog />;
    case 'palette': return <CommandPalette />;
    case 'edit-group': return <GroupEditor groupId={d.groupId} />;
    case 'group-details': return <GroupDetails groupId={d.groupId} />;
    case 'merge': return <MergeDialog personId={d.personId} otherId={d.otherId} />;
    case 'bulk-edit': return <BulkEditDialog personIds={d.personIds} />;
    case 'confirm': return <ConfirmDialog d={d} />;
    case 'media-details': return <MediaDetails mediaId={d.mediaId} draft={d.draft} />;
    case 'edit-source': return <SourceEditor sourceId={d.sourceId} onSave={d.onSave} />;
    case 'edit-todo': return <TodoEditor todoId={d.todoId} personIds={d.personIds} />;
    case 'edit-citation': return <CitationEditor personId={d.personId} target={d.target} index={d.index} sourceId={d.sourceId} />;
  }
}

function Shell() {
  const view = useApp((s) => s.view);
  const selectedId = useApp((s) => s.selectedId);
  const tree = useApp((s) => s.tree)!;
  const canUndo = useApp((s) => s.past.length > 0);
  const canRedo = useApp((s) => s.future.length > 0);
  const saveState = useApp((s) => s.saveState);
  const showPanel = selectedId && tree.people[selectedId] && hasPanel(view);
  const section = sectionOf(view);

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">
            <GitFork size={16} />
          </span>
          <span className="hide-sm">Relationships</span>
        </div>
        <div className="tree-switch">
          <button className="btn ghost" onClick={() => openDialog({ type: 'trees' })} title="Switch or manage trees">
            <span>{tree.name}</span>
            <ChevronDown size={14} />
          </button>
        </div>
        <button className="search-trigger" onClick={() => openDialog({ type: 'palette' })} aria-label="Find a person or command">
          <Search size={15} />
          <span className="grow">Find a person…</span>
          <kbd className="hide-sm">{isMac ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
        <span className="spacer" />
        <SaveIndicator state={saveState} />
        <button className="btn icon ghost" onClick={undo} disabled={!canUndo} aria-label="Undo" title={`Undo (${isMac ? '⌘Z' : 'Ctrl+Z'})`}>
          <Undo2 size={17} />
        </button>
        <button className="btn icon ghost hide-sm" onClick={redo} disabled={!canRedo} aria-label="Redo" title={`Redo (${isMac ? '⇧⌘Z' : 'Ctrl+Y'})`}>
          <Redo2 size={17} />
        </button>
        <button className="btn ghost" onClick={startSync} title="Sync your trees with another device">
          <RefreshCw size={16} /> <span className="hide-md">Sync</span>
        </button>
        <button className="btn ghost" onClick={() => openDialog({ type: 'import', mode: 'merge' })} title={`Add people from a GEDCOM, MacFamilyTree, or Relationships file to ${tree.name}`}>
          <FileUp size={16} /> <span className="hide-md">Import</span>
        </button>
        <button className="btn ghost" onClick={() => openDialog({ type: 'export' })} title="Export or back up">
          <Download size={16} /> <span className="hide-md">Export</span>
        </button>
        <button className="btn icon ghost" onClick={() => openDialog({ type: 'settings' })} aria-label="Settings" title="Settings">
          <Settings size={17} />
        </button>
        <button className="btn icon ghost hide-sm" onClick={() => openDialog({ type: 'help' })} aria-label="Help" title="Help (?)">
          <CircleHelp size={17} />
        </button>
      </header>
      <div className="body">
        {/* Desktop shows every view, grouped by section; phones show the four sections, with their views in a bar above. */}
        <nav className="rail" aria-label="Views">
          {SECTIONS.map((sec, si) => (
            <Fragment key={sec.id}>
              {si > 0 && <span className="rail-sep rail-wide" aria-hidden />}
              {sec.views.map((v) => {
                const { label, long, sub, Icon } = VIEW_INFO[v];
                const full = long ?? label;
                return (
                  <button
                    key={v}
                    className={`rail-btn rail-wide ${flagSlot(ALL_VIEWS.indexOf(v))}`}
                    aria-current={view === v ? 'page' : undefined}
                    aria-label={full}
                    onClick={() => setView(v)}
                    title={viewKey(v) ? `${full} (${viewKey(v)})` : full}
                  >
                    <Icon size={20} />
                    {label}
                    {sub && <span className="rail-sub">{sub}</span>}
                  </button>
                );
              })}
            </Fragment>
          ))}
          {SECTIONS.map((sec, si) => {
            const Icon = SECTION_ICONS[sec.id];
            return (
              <button key={sec.id} className={`rail-btn rail-narrow ${flagSlot(si)}`} aria-current={section === sec.id ? 'page' : undefined} onClick={() => openSection(sec.id)}>
                <Icon size={20} />
                {sec.label}
              </button>
            );
          })}
        </nav>
        <div className="section-tabs" role="tablist" aria-label={`${SECTIONS.find((x) => x.id === section)!.label} views`}>
          {SECTIONS.find((x) => x.id === section)!.views.map((v, vi) => {
            const { label, long, Icon } = VIEW_INFO[v];
            return (
              <button
                key={v}
                role="tab"
                className={`section-tab ${flagSlot(vi)}`}
                aria-selected={view === v}
                onClick={() => setView(v)}
                // A bar wider than the phone scrolls; keep the open view's tab in sight.
                ref={(el) => {
                  if (el && view === v) el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
                }}
              >
                <Icon size={15} />
                {long ?? label}
              </button>
            );
          })}
        </div>
        <main className="main">
          <ViewBoundary key={view}>
            {view === 'tree' && <TreeView />}
            {view === 'network' && <NetworkView />}
            {view === 'people' && <PeopleView />}
            {view === 'relate' && <RelateView />}
            {view === 'groups' && <GroupsView />}
            {view === 'reports' && <ReportsView />}
            {view === 'countdowns' && <CountdownsView />}
            {view === 'map' && (
              <Suspense
                fallback={
                  <div className="empty">
                    <Loader2 className="spin" /> Loading the map…
                  </div>
                }
              >
                <MapView />
              </Suspense>
            )}
            {view === 'media' && <MediaView />}
            {view === 'sources' && <SourcesView />}
            {view === 'todos' && <TodosView />}
          </ViewBoundary>
        </main>
        {showPanel && <PersonPanel key={selectedId} personId={selectedId} />}
      </div>
    </div>
  );
}

function SaveIndicator({ state }: { state: string }) {
  if (state === 'saving') return <span className="save-state hide-sm"><Loader2 size={13} /> Saving…</span>;
  if (state === 'error') return <span className="save-state error"><AlertTriangle size={13} /> Not saved</span>;
  if (state === 'memory') return <span className="save-state memory" title="This browser isn't allowing storage. Export to keep your work."><AlertTriangle size={13} /> Not stored</span>;
  return <span className="save-state hide-sm"><Check size={13} /> Saved</span>;
}
